<template>
  <div
    class="zm-radio-group"
    :class="[size ? 'zm-radio-group--' + size : '']"
    role="radiogroup"
    ref="radioGroup"
    @keydown="handleKeydown"
  >
    <slot></slot>
    <div :id="uid" class="zm-sr-only">Press enter or space to select</div>
  </div>
</template>
<script>
  import Emitter from '../../mixins/emitter';
  import AriaUtils from '../../utils/aria-utils';

  let _uid = 0;

  function uid() {
    return _uid++;
  }

  export default {
    name: 'ZmRadioGroup',

    componentName: 'ZmRadioGroup',

    inject: {
      ZmFormItem: {
        default: ''
      }
    },

    mixins: [Emitter],

    props: {
      value: {},
      size: String,
      fill: String,
      textColor: String,
      disabled: Boolean,
      name: String,
      enterSelect: Boolean
    },

    computed: {
      _elFormItemSize() {
        return (this.ZmFormItem || {}).elFormItemSize;
      },
      radioGroupSize() {
        return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
      },
      uid() {
        return 'zm-radio-group' + uid();
      }
    },

    created() {
      this.$on('handleChange', value => {
        this.$emit('change', value);
      });
    },
    mounted() {
      // 当radioGroup没有默认选项时，第一个可以选中Tab导航
      this.radios = [];
      const nodeListRadios = this.$refs.radioGroup.querySelectorAll('[type=radio]');
      for (let i = 0; i < nodeListRadios.length; i++) {
        this.radios.push(nodeListRadios[i]);
      }
      this.firstRadio = this.radios[0];
      const size = this.radios.length;
      this.lastRadio = this.radios[size - 1];
      if (![].some.call(this.radios, radio => radio.checked) && this.firstRadio) {
        this.firstRadio.tabIndex = 0;
      }
    },
    methods: {
      handleKeydown(e) { // 左右上下按键 可以在radio组内切换不同选项
        const target = e.target;
        const keyCode = e.keyCode;
        const keys = AriaUtils.keys;

        let flag = false;
        switch (keyCode) {
          case keys.space:
          case keys.enter:
            this.setChecked(target);
            flag = true;
            break;
          case keys.left:
          case keys.up:
            this.setCheckedToPreviousItem(target);
            flag = true;
            break;
          case keys.right:
          case keys.down:
            this.setCheckedToNextItem(target);
            flag = true;
            break;
          default:
            break;
        }
        if (flag) {
          e.stopPropagation();
          e.preventDefault();
        }
      },
      setChecked(currentItem, withoutClick) {
        if (!withoutClick) {
          currentItem.click();
        }
        currentItem.focus();
      },
      setCheckedToPreviousItem(currentItem) {
        let index;
        if (currentItem === this.firstRadio) {
          this.setChecked(this.lastRadio, this.enterSelect);
        } else {
          index = this.radios.indexOf(currentItem);
          this.setChecked(this.radios[index - 1], this.enterSelect);
        }
      },
      setCheckedToNextItem(currentItem) {
        let index;
        if (currentItem === this.lastRadio) {
          this.setChecked(this.firstRadio, this.enterSelect);
        } else {
          index = this.radios.indexOf(currentItem);
          this.setChecked(this.radios[index + 1], this.enterSelect);
        }
      }
    },
    watch: {
      value(value) {
        this.dispatch('ZmFormItem', 'el.form.change', [this.value]);
      }
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/mixins/button";
  @import "../../scss/common/var";

  @include b(radio-group) {
    vertical-align: middle;
    &[vertical] {
      display: block;
    }
    &[vertical] .zm-radio {
      display: block;
      margin-right: 0;
      & + .zm-radio {
        margin-bottom: 0;
      }
    }
    &[buttonstyle] {
      display: inline-flex;
    }
    &[buttonstyle] .zm-radio {
      margin-right: 0;
      margin-bottom: 0;
      .zm-radio-wrap {
        padding-left: 0;
      }
      .zm-radio__inner {
        display: none;
      }

      .zm-radio__label {
        display: inline-block;
        white-space: nowrap;
        vertical-align: middle;
        border: $--border-base;
        border-left: 0;
        color: $--color-text-primary;
        -webkit-appearance: none;
        text-align: center;
        box-sizing: border-box;
        outline: none;
        margin: 0;
        position: relative;
        cursor: pointer;
        transition: $--all-transition;
        line-height: $--button-height - 2;
        @include button-size(0, $--button-padding-horizontal, $--button-font-size, 0);

        &:hover {
          color: $--color-primary;
        }

        & [class*="zm-icon-"] {
          line-height: 0.9;

          & + span {
            margin-left: 5px;
          }
        }
      }

      &:first-child {
        .zm-radio__label {
          border-left: $--border-base;
          border-radius: $--border-radius-base 0 0 $--border-radius-base;
        }
      }

      .zm-radio__original {
        &:checked {
          & ~ .zm-radio__label {
            color: $--radio-button-checked-color;
            background-color: $--radio-button-checked-fill;
            border-color: $--radio-button-checked-border-color;
            box-shadow: -1px 0 0 0 $--radio-button-checked-border-color;
          }
        }

        &:disabled {
          & ~ .zm-radio__label {
            color: $--button-disabled-color;
            cursor: not-allowed;
            background-image: none;
            background-color: $--button-disabled-fill;
            box-shadow: none;
          }
          &:checked ~ .zm-radio__label {
            background-color: $--radio-button-disabled-checked-fill;
          }
        }
      }

      &:last-of-type {
        .zm-radio__label {
          border-radius: 0 $--border-radius-base $--border-radius-base 0;
        }
      }

      &:first-child:last-of-type {
        .zm-radio__label {
          border-radius: $--border-radius-base;
        }
      }
    }
    @include m(large) {
      &[buttonstyle] .zm-radio .zm-radio__label {
        line-height: $--button-lg-height - 2;
        @include button-size(0, $--button-lg-padding-horizontal, $--button-lg-font-size, 0);
      }
    }
    @include m(small) {
      &[buttonstyle] .zm-radio .zm-radio__label {
        line-height: $--button-small-height - 2;
        @include button-size(0, $--button-small-padding-horizontal, $--button-small-font-size, 0);
      }
    }
  }

  .is-keyboard-event .zm-radio-group[buttonstyle] .zm-radio__original:focus:not(:active):not(:disabled) ~ .zm-radio__label {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px $--color-primary;
  }
</style>
